import { useField } from "formik";
import { FC, useMemo } from "react";

import __ from "web/Layout/Translations";

import Field from "web/Components/Common/Form/Field";
import ValidationErrorMessage from "web/Components/Common/ValidationErrorMessage";

import {
  DEFAULT_PHONE_PREFIX,
  getPhonePrefixOptions,
} from "web/utils/page/product/subscription/forms/getPhonePrefixOptions";

import type { PhoneWithPrefixField } from "web/types/Subscription";

import styles from "./phoneWithPrefixForm.scss";

interface IPhoneWithPrefixFormProps {
  field: PhoneWithPrefixField;
  tooltipMessage?: string;
  disabled?: boolean;
  classes?: {
    label?: string;
    tooltipWrapper?: string;
    tooltip?: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ToolTip?: any;
  lang?: {
    key: string;
    code: string;
  };
}

const labels = {
  pl: {
    phoneNumber: "Wpisz numer telefonu",
  },
  en: {
    phoneNumber: "Enter phone number",
  },
};

const PhoneWithPrefixForm: FC<IPhoneWithPrefixFormProps> = ({
  field,
  tooltipMessage,
  disabled = false,
  classes = {},
  ToolTip,
  lang = { key: "pl_PL", code: "pl" },
}) => {
  const langLabels = labels[lang.code as keyof typeof labels] || labels.pl;

  const prefixFieldName = `${field.id}.prefix`;
  const phoneNumberFieldName = `${field.id}.phoneNumber`;

  const [prefixField, , prefixHelpers] = useField(prefixFieldName);
  const [, phoneMeta] = useField(phoneNumberFieldName);

  const prefixOptions = useMemo(() => getPhonePrefixOptions(), []);

  const doesFieldHaveTooltip = tooltipMessage?.trim();
  const phoneError = phoneMeta.error && phoneMeta.touched;

  return (
    <>
      {field.label && (
        <div className={classes.tooltipWrapper || styles.tooltipWrapper}>
          <dl className={classes.label || styles.label}>
            {__(field.label)}
            {doesFieldHaveTooltip && ToolTip && (
              <ToolTip
                customStyles={{
                  root: classes.tooltipWrapper || styles.tooltipWrapper,
                  tooltip: classes.tooltip || styles.tooltip,
                }}
                message={tooltipMessage}
                variant="right"
              />
            )}
          </dl>
        </div>
      )}
      <div className={styles.wrapper}>
        <div className={styles.prefixWrapper}>
          <select
            className={styles.select}
            value={prefixField.value || DEFAULT_PHONE_PREFIX}
            onChange={(e) => prefixHelpers.setValue(e.target.value)}
            onBlur={() => prefixHelpers.setTouched(true)}
            data-t1={`${field.id}-prefix`}
            disabled={disabled}
          >
            {prefixOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.phoneWrapper}>
          <Field
            name={phoneNumberFieldName}
            placeholder={__(langLabels.phoneNumber)}
            dataT1={`${field.id}-phoneNumber`}
            isDataT1NotChanged
            hideError
            disabled={disabled}
          />
        </div>
      </div>
      {phoneError && (
        <div className={styles.errorContainer}>
          <ValidationErrorMessage name={phoneNumberFieldName} />
        </div>
      )}
    </>
  );
};

export default PhoneWithPrefixForm;
